import type { AppState } from 'src/reducers';
import { createSelector, type Selector } from 'reselect';
import type { SortOption } from 'src/common-ui/components/CompanionListView/CompanionListView.types';
import { parseActions } from './utils/ConfigurableGrid.utils';
import type { ClientActionHandlersConfig, ConfigurableGridColumnDef } from './ConfigurableGrid.types';
import type { ConfigurableGridOwnProps } from 'configuration-schemas/ownProps';
import type { SubheaderDropdownProps } from '../Subheader/SubheaderDropdown';
import { cloneDeep, defaultTo, get, isNil } from 'lodash';
import type { BasicItem, BasicPivotItem } from 'src/worker/pivotWorker.types';
import { filterData } from 'src/utils/Pivot/Filter';
import { externalGridSearchFields } from 'src/utils/Domain/Constants';
import type { ConfigurableGridGroupBySelection } from './ConfigurableGrid.slice';
import { flattenToLeaves } from 'src/utils/Pivot/Flatten';
import { generateOrderedGroups, type GridItem } from 'src/utils/Component/AgGrid/AgDataFormat';
import { groupedToAgFlatTree } from 'src/utils/Component/AgGrid/AgDataFormat';
import { type GroupBySlice, initialSortBy } from '../Subheader/Subheader.slice';
import { getGroupBy } from '../Subheader/Subheader.selectors';
import type { ConfigurableGridViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import { blacklistByDataIndex } from 'src/utils/Component/AttributeBlacklist.utils';
import type { AttributeBlacklistConfig, BasicViewItem } from 'configuration-schemas/viewdefns/general';
import type { ColDef } from '@ag-grid-community/core';

export interface SelectorSubheaderDropdownProps extends Omit<SubheaderDropdownProps, 'handleChangeOnDropdown'> {}

export interface ConfigurableGridDataSelectorProps {
  showFlowStatus: boolean;
  leafId: string;
}

const getColumnDefs: Selector<AppState, ConfigurableGridColumnDef[] | undefined> = (state) => {
  return state.pages.assortmentBuild.configurableGrid.viewDefn?.columnDefs;
};

// FIXME: the viewdefn needs to be properly typed
const getCompanionSortConfig: Selector<AppState, any> = (state) => {
  return state.pages.assortmentBuild.configurableGrid.viewDefn?.companionGrid.sortConfig;
};

const getActions: Selector<AppState, ClientActionHandlersConfig | undefined> = (state) => {
  return state.pages.assortmentBuild.configurableGrid.viewDefn?.actions;
};

const getGroupByDropdownConfig: Selector<AppState, SelectorSubheaderDropdownProps | undefined> = (state) => {
  return state.pages.assortmentBuild.configurableGrid.viewDefn?.subheaderDropdowns.groupBy;
};

export const getGroupBySelection: Selector<AppState, ConfigurableGridGroupBySelection | undefined> = (state) => {
  if (isNil(getGroupByDropdownConfig(state))) {
    return undefined;
  }
  const groupBy = getGroupBy(state);
  const selectedIndex = groupBy.selection ?? 0;
  const option = groupBy.options[selectedIndex];
  return {
    option,
    selectedIndex,
  };
};

export const getGraphGroupByDropdownConfig: Selector<AppState, GroupBySlice> = (state: AppState) => {
  return state.pages.assortmentBuild.configurableGrid.graphGroupBy;
};

export const getGraphGroupBySelection = (state: AppState): ConfigurableGridGroupBySelection | undefined => {
  const maybeGroupBy = getGraphGroupByDropdownConfig(state);
  if (isNil(maybeGroupBy)) {
    return undefined;
  }
  const selectedIndex = maybeGroupBy.selection ?? 0;
  const option = maybeGroupBy.options[selectedIndex];
  return {
    option,
    selectedIndex,
  };
};

export const getFloorsetData: Selector<AppState, BasicItem[]> = (state) => {
  return state.subheader.topMemberOptions ?? [];
};

export const getSelectedFloorset: Selector<AppState, string | undefined> = (state) => {
  const { topMemberType, topMemberSelectionByType } = state.subheader;
  return topMemberType ? topMemberSelectionByType?.[topMemberType] : undefined;
};

export const getConfigurableGridData: Selector<AppState, BasicPivotItem[]> = (state) => {
  return state.pages.assortmentBuild.configurableGrid.gridData;
};

const getShowFlowStatus: Selector<AppState, boolean, [ConfigurableGridDataSelectorProps]> = (
  _state,
  additionalParams,
) => {
  const { showFlowStatus } = additionalParams;
  return showFlowStatus;
};

const getFlowStatus: Selector<AppState, number[]> = (state) => {
  return state.subheader.flowStatus;
};

const getSearch: Selector<AppState, string> = (state) => {
  return state.subheader.search;
};

const getRawViewDefn: Selector<AppState, ConfigurableGridViewDefn> = (state) => {
  return state.pages.assortmentBuild.configurableGrid.viewDefn;
};

export const getAttributeBlacklist: Selector<AppState, AttributeBlacklistConfig | undefined> = (state: AppState) => {
  return state.subheader.attributeBlacklist;
};

export const getCurrentDepartment: Selector<AppState, string | null> = (state: AppState) => {
  return state.scope.scope.productMember;
};

export const getViewDefn = createSelector(
  getRawViewDefn,
  getAttributeBlacklist,
  getCurrentDepartment,
  (viewDefn, blacklist, currentDepartment): ConfigurableGridViewDefn => {
    if (!viewDefn || !blacklist || !currentDepartment) {
      return viewDefn;
    }
    const withColumnDefs = viewDefn as ConfigurableGridViewDefn & { columnDefs?: ConfigurableGridColumnDef[] };
    return {
      ...viewDefn,
      columnDefs: withColumnDefs.columnDefs
        ? blacklistByDataIndex(withColumnDefs.columnDefs, blacklist, currentDepartment)
        : withColumnDefs.columnDefs,
      columns: viewDefn.columns
        ? blacklistByDataIndex(viewDefn.columns, blacklist, currentDepartment)
        : viewDefn.columns,
    } as ConfigurableGridViewDefn;
  },
);

const getLeafId: Selector<AppState, string, [ConfigurableGridDataSelectorProps]> = (_state, additionalParams) => {
  const { leafId } = additionalParams;
  return leafId;
};

export const getLeafIdentifier: Selector<ConfigurableGridOwnProps, string> = (ownProps) => {
  return ownProps.keys.leafIdProp ?? ownProps.keys.idProp;
};

export const getViewDefnData = createSelector(
  getColumnDefs,
  getCompanionSortConfig,
  getActions,
  getAttributeBlacklist,
  getCurrentDepartment,
  (columnDefs, sortConfig, actions, blacklist, currentDepartment) => {
    const filteredColumnDefs =
      columnDefs && blacklist && currentDepartment
        ? (blacklistByDataIndex(
            columnDefs as unknown as BasicViewItem[],
            blacklist,
            currentDepartment,
          ) as unknown as ConfigurableGridColumnDef[])
        : columnDefs;

    const sortConfigView = sortConfig?.view as BasicViewItem[] | undefined;
    const filteredSortView =
      sortConfigView && blacklist && currentDepartment
        ? blacklistByDataIndex(sortConfigView, blacklist, currentDepartment)
        : sortConfigView;

    // generate dependent calc object
    const dependentCalcs: Record<string, any> = {};
    if (!isNil(filteredColumnDefs)) {
      filteredColumnDefs.forEach((colDef) => {
        if (colDef.dependentCalc) {
          dependentCalcs[colDef.dataIndex] = colDef.dependentCalc;
        }
      });
    }

    // generate companion view props
    const companionSortOptions = (filteredSortView ?? []) as SortOption[];
    const companionSortSelected: SortOption = companionSortOptions.find(
      (opt) => opt.dataIndex === sortConfig?.default,
    ) ??
      companionSortOptions[0] ?? { dataIndex: 'id', text: 'ID' };
    const defaultCompanionSortField = companionSortSelected.dataIndex;

    // generate actionHandlers
    const clientActionHandlers = !isNil(actions) ? parseActions(actions) : {};
    return {
      dependentCalcs,
      companionSortOptions,
      defaultCompanionSortField,
      clientActionHandlers,
    };
  },
);

export const getGroupByDropdownProps = createSelector(
  getGroupBySelection,
  getGroupByDropdownConfig,
  (groupBySelection, dropdownConfig): SelectorSubheaderDropdownProps | undefined => {
    if (isNil(dropdownConfig)) {
      return;
    }

    const configCopy = cloneDeep(dropdownConfig);
    configCopy.selection = defaultTo(groupBySelection?.selectedIndex ?? dropdownConfig.defaultSelection, 0);
    return configCopy;
  },
);

interface NeededFilterInfo {
  flowStatus: number[];
  search: string;
  viewDefn: ConfigurableGridViewDefn;
}

export const getNeededFilterInfo = createSelector(
  getFlowStatus,
  getSearch,
  getViewDefn,

  (flowStatus, search, viewDefn): NeededFilterInfo => {
    return {
      flowStatus,
      search,
      viewDefn,
    };
  },
);

interface GroupedGridData {
  agFlatTree: GridItem[];
  treeColumnDefinition: ColDef;
}

export const getGroupedConfigurableGridData = createSelector(
  getConfigurableGridData,
  getShowFlowStatus,
  getFlowStatus,
  getLeafId,
  getSearch,
  getViewDefn,
  getGroupByDropdownProps,
  (
    gridData,
    showFlowStatus,
    flowStatus,
    leafId,
    search,
    viewDefn,
    groupByDropdownProps,
  ): GroupedGridData | undefined => {
    if (isNil(groupByDropdownProps) || isNil(groupByDropdownProps.selection)) {
      return undefined;
    }

    const configSearchOrDefafult = get(viewDefn, 'searchIndexes', externalGridSearchFields) as string[];
    const selectedGroupBy = groupByDropdownProps.options[groupByDropdownProps.selection];
    const groupByKey = selectedGroupBy.dataIndex;
    const finalFlowStatus = showFlowStatus ? flowStatus : [];
    const filteredGridData = filterData(gridData, search, configSearchOrDefafult, finalFlowStatus);
    const flatData = flattenToLeaves(filteredGridData);
    const groupedItems = generateOrderedGroups(flatData, groupByDropdownProps as GroupBySlice);
    const result = groupedToAgFlatTree(
      groupedItems,
      groupByKey,
      search,
      initialSortBy,
      flowStatus,
      groupByDropdownProps.options,
      leafId === 'id' ? 'name' : `${leafId}`,
      true,
    );

    // add in asyncstate to grouped rows for correct rendering
    const agFlatTree = result.agFlatTree.map((item) => {
      return !item.hasOwnProperty('asyncstate') ? { ...item, asyncstate: {} } : item;
    });

    return { ...result, agFlatTree };
  },
);
